/**
 * The camera control in the add sheet.
 *
 * Deliberately a file input with `capture`, not a getUserMedia viewfinder: on a
 * phone — where you actually are when you're putting shopping away — this opens
 * the real camera app, with its focus, flash and shutter, and hands back a
 * photo. On a laptop the same control opens the file picker. No permission
 * prompts to design, no video element to tear down.
 */

import { useState } from "react";

import { Icon } from "./Icon";
import { usePhotoCapture } from "./usePhotoCapture";

import "./PhotoField.css";

interface PhotoFieldProps {
  /** A data URL, or null when there's no photo yet. */
  photo: string | null;
  onChange: (photo: string | null) => void;
}

export function PhotoField({ photo, onChange }: PhotoFieldProps) {
  const [error, setError] = useState<string | null>(null);

  const { inputRef, isWorking, handleChange, open } = usePhotoCapture({
    onPhoto: (next) => {
      setError(null);
      onChange(next);
    },
    onError: setError,
  });

  return (
    <div className="photo">
      <span className="photo__label">Photo</span>

      {/* One input, always mounted, opened by whichever control is on screen. */}
      <input
        ref={inputRef}
        className="sr-only"
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleChange}
      />

      {photo ? (
        <div className="photo__preview">
          <img className="photo__image" src={photo} alt="" />
          <button
            className="photo__retake"
            type="button"
            onClick={open}
          >
            Retake
          </button>
          <button
            className="photo__remove"
            type="button"
            onClick={() => {
              setError(null);
              onChange(null);
            }}
            aria-label="Remove photo"
          >
            <Icon name="close" />
          </button>
        </div>
      ) : (
        <button
          className="photo__add"
          type="button"
          disabled={isWorking}
          onClick={open}
        >
          <Icon name="camera" className="photo__add-icon" />
          {isWorking ? "Adding…" : "Add photo"}
        </button>
      )}

      {error && (
        <p className="photo__error" role="alert">
          <Icon name="alert" className="photo__error-icon" />
          {error}
        </p>
      )}
    </div>
  );
}
